// Per-screen help copy, written in plain language for non-engineers (the
// "ANANSI, approachable" foundation). Keep each line to one calm sentence or
// two; avoid engineering jargon (use the glossary + <InfoTip> for terms that
// need defining). Keys are route paths; pageHelpFor falls back from /x/:id → /x.

export const pageDocs: Record<string, string> = {
  "/clients":
    "Each client and what they have connected. Open a client to see its connections, pipelines, activity and settings.",
  "/clients/:id":
    "One client, end to end. The tabs cover its pipelines, connections, activity and settings; the environment selector narrows all of them.",
  "/activity":
    "Every record that moved through a pipeline — one row per fan, order or contact, with whether it synced. Refreshes automatically.",
  "/activity/:id":
    "One record's trip through its pipeline, stage by stage, with the data at each step. Re-run sends it through again.",
  "/attention":
    "Events that could not be processed and are waiting for you. Resend them once the cause is fixed, or discard them.",
  "/overview":
    "A live picture of everything syncing right now — clients, syncs, and recent activity. The numbers refresh on their own.",
  "/pipelines":
    "Syncs that keep two connected apps in step — when a record changes in one, it's copied to the other automatically.",
  "/pipelines/new":
    "Set up a sync step by step: choose where data comes from, what gets copied, and where it goes. Each choice narrows the next.",
  "/connections":
    "The apps a client has connected — a HubSpot portal, a Jump environment, a data warehouse. Syncs move data between them.",
  "/connections/new":
    "Pick the kind of app to connect, then fill in what it needs to sign in. Required fields are marked.",
  "/catalog":
    "The menu of app types and mappings available in this build. Open one to see how to set it up and what access it needs.",
  "/environments":
    "A way to label a sync for a client or stage (like dev or production). It's just a tag on the data, not a separate system.",
  "/secrets":
    "Stored sign-in credentials the connected apps use. They're kept encrypted; platform-level ones are read-only here.",
  "/glossary": "Plain-language definitions for the terms used across ANANSI.",
};

/**
 * The help line for a path, or null when that route has none.
 *
 * An exact entry wins; otherwise trailing segments are dropped until one
 * matches, so `/pipelines/:id` inherits the copy written for `/pipelines`
 * instead of leaving a detail page with nothing.
 */
export function pageHelpFor(pathname: string): string | null {
  if (pageDocs[pathname]) return pageDocs[pathname];
  // strip trailing /<id> segments and try again — covers /pipelines/:id, /runs/:id etc.
  const parts = pathname.split("/").filter(Boolean);
  while (parts.length > 0) {
    parts.pop();
    const fallback = "/" + parts.join("/");
    if (pageDocs[fallback]) return pageDocs[fallback];
  }
  return null;
}
